// @ts-check
import { onDomReady } from '../utils/dom';
import * as ScrollUtil from '../utils/scrolling';

/**
 * Aufklappbare Bereiche: Schalter = Link/Button mit data-collapse="<id>" (Template-Variante
 * content_element/hyperlink/collapse-toggle), Ziel = Element mit dieser ID und Klasse .cf-collapse.
 * - Der Link behält sein href als Fallback ohne JavaScript; mit JavaScript wird der Klick abgefangen.
 * - Geschlossene Bereiche sind inert (nicht fokussierbar, nicht im Accessibility-Tree).
 * - iframes mit data-src werden erst beim ersten Öffnen geladen (Datenschutz + keine Ladelast beim Seitenaufbau);
 *   data-resizer = URL eines iframe-resizer-Skripts, das nach dem Laden auf den iframe angewendet wird.
 * - Cookiebar-Kopplung (oveleon/contao-cookiebar): trägt ein iframe zusätzlich data-consent="<tl_cookie.id>", gilt
 *   die Einwilligung aus dem Cookiebanner für diesen Bereich: Ist der Cookie in der Cookiebar bestätigt (Seitenaufruf
 *   oder cookiebar_save), öffnet sich der Bereich von selbst und lädt den iframe ohne zweiten Klick; wird die
 *   Einwilligung im Banner entzogen, schließt der Bereich und der iframe wird entladen. Umgekehrt meldet der Klick
 *   auf den Schalter („Akzeptieren und Laden") die Einwilligung per cookiebar.push() an die Cookiebar zurück.
 */

/** @type {Map<string, Promise<void>>} */
const scripts = new Map();

/** iframes, auf die iframe-resizer schon angewendet wurde (doppelter Aufruf erzeugt eine Warnung) */
const resized = new WeakSet();

/**
 * @typedef {object} Collapse
 * @property {HTMLElement} toggle
 * @property {HTMLElement} panel
 * @property {number[]} consentIds  Cookie-IDs (tl_cookie) aus data-consent der iframes im Bereich
 */

/**
 * @typedef {object} Cookiebar  Ausschnitt der API von oveleon/contao-cookiebar 2.x
 * @property {(cookie: number|string) => boolean} cookieExists
 * @property {(cookieId: number|string) => void} push
 * @property {(force?: boolean) => void} [updateUserInterface]
 * @property {{ cookies?: Record<string, unknown> }} [settings]
 */

/**
 * @param {string} src
 * @returns {Promise<void>}
 */
function loadScript(src) {
    if (!scripts.has(src)) {
        scripts.set(
            src,
            new Promise((resolve, reject) => {
                const script = document.createElement('script');
                script.src = src;
                script.async = true;
                script.addEventListener('load', () => resolve());
                script.addEventListener('error', () => reject(new Error(`Skript nicht geladen: ${src}`)));
                document.head.append(script);
            }),
        );
    }

    return /** @type {Promise<void>} */ (scripts.get(src));
}

/**
 * @param {HTMLElement} panel
 * @returns {HTMLIFrameElement[]}
 */
function lazyFrames(panel) {
    return Array.from(panel.querySelectorAll('iframe[data-src]')).filter(
        /** @returns {frame is HTMLIFrameElement} */ (frame) => frame instanceof HTMLIFrameElement,
    );
}

/**
 * data-src bleibt am iframe stehen, damit der Inhalt nach einem Entladen (Einwilligung entzogen) erneut geladen werden kann.
 *
 * @param {HTMLElement} panel
 */
function loadLazyContent(panel) {
    for (const frame of lazyFrames(panel)) {
        const { src, resizer } = frame.dataset;

        if (!src || frame.getAttribute('src') === src) continue;

        frame.src = src;

        if (resizer && !resized.has(frame)) {
            resized.add(frame);
            loadScript(resizer)
                .then(() => {
                    // iframe-resizer (KD-Bank-Einbettungscode): passt die Höhe an den Formularinhalt an
                    // @ts-ignore – globales Skript
                    window.iFrameResize?.({ log: false, inPageLinks: true }, frame);
                })
                .catch((error) => console.warn(error));
        }
    }
}

/**
 * @param {HTMLElement} panel
 */
function unloadLazyContent(panel) {
    for (const frame of lazyFrames(panel)) {
        if (frame.hasAttribute('src')) {
            frame.removeAttribute('src');
            frame.style.removeProperty('height');
        }
    }
}

/**
 * @param {Collapse} collapse
 * @param {boolean} open
 * @param {{ scroll?: boolean }} [options]  scroll: nach dem Öffnen zum Bereich scrollen (nur beim Klick, nicht bei
 *                                          Öffnen durch die Cookiebar)
 */
function setState({ toggle, panel }, open, { scroll = true } = {}) {
    panel.classList.toggle('open', open);
    panel.inert = !open;
    toggle.setAttribute('aria-expanded', String(open));

    if (open) {
        loadLazyContent(panel);

        if (scroll) {
            // Panel-Oberkante steht während der Höhenanimation fest → sofort scrollen
            window.setTimeout(() => ScrollUtil.scrollToTarget(panel, -24), 50);
        }
    } else {
        unloadLazyContent(panel);
    }
}

/**
 * @returns {Cookiebar|null}
 */
function getCookiebar() {
    // @ts-ignore – globale Variable aus dem Konfigurationsskript der Cookiebar
    const cookiebar = window.cookiebar;

    return cookiebar && typeof cookiebar.cookieExists === 'function' && typeof cookiebar.push === 'function'
        ? cookiebar
        : null;
}

/**
 * Einwilligung aus dem Schalter-Klick an die Cookiebar melden (nur Cookies, die diese Konfiguration kennt).
 *
 * @param {Collapse} collapse
 */
function grantConsent({ consentIds }) {
    const cookiebar = getCookiebar();

    if (!cookiebar || !consentIds.length) return;

    let pushed = false;

    for (const id of consentIds) {
        if (cookiebar.settings?.cookies && !(`_${id}` in cookiebar.settings.cookies)) continue;
        if (cookiebar.cookieExists(id)) continue;

        cookiebar.push(id);
        pushed = true;
    }

    if (pushed) {
        cookiebar.updateUserInterface?.();
    }
}

/**
 * Zustand des Bereichs an die Cookiebar-Einwilligung angleichen.
 *
 * @param {Collapse} collapse
 * @param {Cookiebar} cookiebar
 */
function applyConsent(collapse, cookiebar) {
    const granted = collapse.consentIds.every((id) => cookiebar.cookieExists(id));
    const open = collapse.panel.classList.contains('open');

    if (granted && !open) {
        setState(collapse, true, { scroll: false });
    } else if (!granted && open) {
        setState(collapse, false);
    }
}

/**
 * @returns {Collapse[]}
 */
function initializeCollapses() {
    /** @type {Collapse[]} */
    const collapses = [];

    for (const toggle of document.querySelectorAll('[data-collapse]')) {
        if (!(toggle instanceof HTMLElement)) continue;

        const panel = document.getElementById(toggle.dataset.collapse ?? '');

        if (!panel) continue;

        const consentIds = lazyFrames(panel)
            .map((frame) => Number.parseInt(frame.dataset.consent ?? '', 10))
            .filter((id) => Number.isInteger(id) && id > 0);

        /** @type {Collapse} */
        const collapse = { toggle, panel, consentIds };

        panel.classList.add('cf-collapse--js');
        panel.inert = true;

        toggle.addEventListener('click', (event) => {
            event.preventDefault();
            const open = !panel.classList.contains('open');
            setState(collapse, open);

            if (open) {
                grantConsent(collapse);
            }
        });

        collapses.push(collapse);
    }

    return collapses;
}

/**
 * @param {Collapse[]} collapses
 */
function bindCookiebar(collapses) {
    const gated = collapses.filter((collapse) => collapse.consentIds.length > 0);

    if (!gated.length) return;

    /** @param {Cookiebar|null} cookiebar */
    const sync = (cookiebar) => {
        if (!cookiebar) return;
        gated.forEach((collapse) => applyConsent(collapse, cookiebar));
    };

    // Das Konfigurationsskript der Cookiebar steht am Ende des <body> und läuft vor diesem Modul (type="module" ist
    // deferred) → cookiebar_init ist meist schon vorbei. Beide Wege abdecken:
    sync(getCookiebar());

    for (const type of ['cookiebar_init', 'cookiebar_save']) {
        window.addEventListener(type, (event) => {
            const detail = /** @type {CustomEvent<{ cookiebar?: Cookiebar }>} */ (event).detail;
            sync(detail?.cookiebar ?? getCookiebar());
        });
    }
}

onDomReady(() => {
    bindCookiebar(initializeCollapses());
});
